import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

const NotificationContext = createContext({});

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadProposals, setUnreadProposals] = useState(0);
  const [unreadOrders, setUnreadOrders] = useState(0);

  // Fetch unread message count
  const fetchUnreadMessages = useCallback(async () => {
    if (!user) {
      setUnreadMessages(0);
      return;
    }

    try {
      // Get all conversations where user is participant
      const { data: conversations } = await supabase
        .from('conversations')
        .select('id')
        .or(`participant_1.eq.${user.id},participant_2.eq.${user.id}`);

      if (!conversations || conversations.length === 0) {
        setUnreadMessages(0);
        return;
      }

      const conversationIds = conversations.map(c => c.id);

      // Count unread messages in those conversations (not sent by current user)
      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .in('conversation_id', conversationIds)
        .neq('sender_id', user.id)
        .eq('read', false);

      if (error) throw error;
      setUnreadMessages(count || 0);
    } catch (error) {
      console.error('Error fetching unread messages:', error);
    }
  }, [user]);

  // Fetch unread proposals (for crafters - new pending proposals)
  const fetchUnreadProposals = useCallback(async () => {
    if (!user) {
      setUnreadProposals(0);
      return;
    }

    try {
      // Count pending proposals on the client's own requests
      const { count, error } = await supabase
        .from('proposals')
        .select('*, direct_requests!inner(client_id)', { count: 'exact', head: true })
        .eq('direct_requests.client_id', user.id)
        .eq('status', 'pending');

      if (error) {
        setUnreadProposals(0);
        return;
      }
      
      setUnreadProposals(count || 0);
    } catch (error) {
      console.error('Error fetching unread proposals:', error);
      setUnreadProposals(0);
    }
  }, [user]);

  // Initial fetch
  useEffect(() => {
    if (user) {
      fetchUnreadMessages();
      fetchUnreadProposals();
    } else {
      setUnreadMessages(0);
      setUnreadProposals(0);
      setUnreadOrders(0);
    }
  }, [user, fetchUnreadMessages, fetchUnreadProposals]);

  // Set up real-time subscription for new messages
  useEffect(() => {
    if (!user) return;

    // Create a unique channel name per user to avoid conflicts
    const channelName = `notifications-${user.id}`;
    
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        (payload) => {
          // Only increment if message is not from current user
          if (payload.new.sender_id !== user.id) {
            console.log('New message received via realtime:', payload.new);
            setUnreadMessages(prev => prev + 1);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `read=eq.true`
        },
        () => {
          // Refresh count when messages are marked as read
          fetchUnreadMessages();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'proposals'
        },
        () => {
          // New proposal received
          fetchUnreadProposals();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'orders'
        },
        () => {
          // New order - could notify crafter
          setUnreadOrders(prev => prev + 1);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'conversations'
        },
        (payload) => {
          // New conversation created - check if user is participant
          if (payload.new.participant_1 === user.id || payload.new.participant_2 === user.id) {
            console.log('New conversation for user:', payload.new);
            // Refresh messages after a short delay to catch the first message
            setTimeout(() => fetchUnreadMessages(), 1000);
          }
        }
      )
      .subscribe((status) => {
        console.log('Realtime subscription status:', status);
      });

    // Backup polling every 10 seconds in case realtime fails
    const pollInterval = setInterval(() => {
      fetchUnreadMessages();
    }, 10000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollInterval);
    };
  }, [user, fetchUnreadMessages, fetchUnreadProposals]);

  // Mark messages as read
  const markMessagesAsRead = useCallback(async (conversationId) => {
    if (!user) return;

    try {
      await supabase
        .from('messages')
        .update({ read: true })
        .eq('conversation_id', conversationId)
        .neq('sender_id', user.id)
        .eq('read', false);

      // Refresh count
      fetchUnreadMessages();
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }, [user, fetchUnreadMessages]);

  // Clear notification counts
  const clearMessageNotifications = useCallback(() => {
    fetchUnreadMessages();
  }, [fetchUnreadMessages]);

  const totalNotifications = unreadMessages + unreadProposals + unreadOrders;

  const value = {
    unreadMessages,
    unreadProposals,
    unreadOrders,
    totalNotifications,
    fetchUnreadMessages,
    fetchUnreadProposals,
    markMessagesAsRead,
    clearMessageNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
